/**
 * Shared Konva-stage setup and screenshot-capture logic used by every
 * DICOM-style 2D viewport (DicomVisualization + OrthogonalCprVisualization).
 */

import Konva from 'konva';

/**
 * Create the Konva stage + transparent overlay container used for drawing
 * annotations on top of the VTK canvas.  The overlay container is appended
 * inside `viewContainer` and the returned stage renders into it.
 *
 * Caller is expected to:
 *   - store the returned stage somewhere accessible (e.g. context.current)
 *   - size the stage via stage.size() / resize events
 *   - destroy the stage when the viewport unmounts
 *
 * @param {HTMLElement} viewContainer - The viewport's outer div.
 * @param {string} viewportId - Identifier used to namespace the overlay div.
 * @returns {{stage: import('konva').Stage, konvaContainer: HTMLDivElement}}
 */
export function createKonvaStage(viewContainer, viewportId) {
    const konvaContainer = document.createElement('div');
    konvaContainer.id = `dicom-konva-container-${viewportId}`;
    konvaContainer.style.position = 'absolute';
    konvaContainer.style.top = '0';
    konvaContainer.style.left = '0';
    konvaContainer.style.width = '100%';
    konvaContainer.style.height = '100%';
    konvaContainer.style.pointerEvents = 'none';
    konvaContainer.style.zIndex = '1';
    viewContainer.appendChild(konvaContainer);

    const stage = new Konva.Stage({
        container: konvaContainer,
        width: viewContainer.offsetWidth,
        height: viewContainer.offsetHeight,
    });

    return { stage, konvaContainer };
}

/**
 * Compose a "custom" screenshot: a PNG data-URL made from the VTK canvas,
 * the Konva annotation stage, and any SVGs rendered in the DOM by VTK
 * widgets (brush outlines, distance widgets, etc.).
 *
 * Rejects if the viewport canvas is not available or the viewport is not
 * being shown.
 *
 * @param {Object} context - Must contain genericRenderWindow, renderWindow,
 *                           and optionally konvaStage.
 * @param {string} viewportId
 * @returns {Promise<{viewportIndex: string, canvasData: string}>}
 */

// TODO: Potential refactor: move all screenshot capture logics (_captureCustomScreenshot) in CPR and mesh visualizations to DicomViewportShared.js
export function composeCustomScreenshot(context, viewportId) {
    return new Promise((resolve, reject) => {
        context.renderWindow.render();
        const viewportCanvas = context.genericRenderWindow
            .getApiSpecificRenderWindow()
            .getCanvas();
        if (!viewportCanvas) {
            reject(`Unable to find canvas for viewport ${viewportId}`);
            return;
        }

        const dpr = window.devicePixelRatio;

        const offscreenCanvas = document.createElement('canvas');
        // SVGs do not respect the scaling passed into CanvasContext.drawImage, so instead of doing a bunch of
        // futzing to force the SVG to scale up with the DPR, we will just scale the canvas down.
        // TODO: this approach results in a lower quality image, we should fix it later when we have more time
        offscreenCanvas.width = viewportCanvas.width / dpr;
        offscreenCanvas.height = viewportCanvas.height / dpr;

        if (viewportCanvas.width === 0 || viewportCanvas.height === 0) {
            reject(
                `Trying to take a screenshot of a viewport ${viewportId}, not being shown`,
            );
            return;
        }

        const offscreenCtx = offscreenCanvas.getContext('2d');
        offscreenCtx.drawImage(
            viewportCanvas,
            0,
            0,
            offscreenCanvas.width,
            offscreenCanvas.height,
        );

        const konvaStage = context.konvaStage;
        if (konvaStage) {
            const konvaCanvas = konvaStage.toCanvas();
            offscreenCtx.drawImage(
                konvaCanvas,
                0,
                0,
                offscreenCanvas.width,
                offscreenCanvas.height,
            );
        }

        const finalizeScreenshot = () => {
            resolve({
                viewportIndex: viewportId,
                canvasData: offscreenCanvas.toDataURL(),
            });
        };

        const svgElements = Array.from(
            viewportCanvas.parentElement?.querySelectorAll('svg') || [],
        );
        if (svgElements.length === 0) {
            console.warn(
                `Unable to find annotations for viewport ${viewportId}, rendering without annotations`,
            );
            finalizeScreenshot();
            return;
        }

        const drawSvgAtIndex = (index) => {
            if (index >= svgElements.length) {
                finalizeScreenshot();
                return;
            }

            const svg = svgElements[index];
            const outerHTML = svg.outerHTML;
            // VTK is evil and doesn't actually make the tag a valid SVG file.
            const hasNamespace = outerHTML.includes('xmlns=');
            const validSvg = hasNamespace
                ? outerHTML
                : outerHTML.replace(
                    '<svg',
                    '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"',
                );
            const blob = new Blob([validSvg], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);

            const svgImg = new Image();

            svgImg.onload = function () {
                offscreenCtx.drawImage(
                    svgImg,
                    0,
                    0,
                    offscreenCanvas.width,
                    offscreenCanvas.height,
                );
                URL.revokeObjectURL(url);
                drawSvgAtIndex(index + 1);
            };

            const rejectHandler = function (ev) {
                URL.revokeObjectURL(url);
                console.warn(
                    `Unable to render annotations for viewport ${viewportId}, rendering without annotations`,
                    ev,
                );
                drawSvgAtIndex(index + 1);
            };
            svgImg.oncancel = rejectHandler;
            svgImg.onabort = rejectHandler;
            svgImg.onerror = rejectHandler;

            svgImg.width = offscreenCanvas.width;
            svgImg.height = offscreenCanvas.height;
            svgImg.src = url;
        };

        drawSvgAtIndex(0);
    });
}

/**
 * Create the event handler + register it on `window` for "custom"
 * screenshot events.  Returns a cleanup function that unregisters it.
 *
 * The handler is a no-op when the event's viewport id doesn't match.
 *
 * @param {React.MutableRefObject} contextRef - Ref whose .current has
 *        genericRenderWindow/renderWindow/konvaStage when the handler fires.
 * @param {string} viewportId
 * @param {string|undefined} eventName - The DOM event name to subscribe to.
 *        When falsy, the function returns a no-op cleanup.
 * @returns {() => void}
 */
export function attachCustomScreenshotListener(contextRef, viewportId, eventName) {
    if (!eventName) {
        return () => { };
    }

    function handler(ev) {
        if (ev.detail.viewportId !== viewportId) {
            return;
        }

        if (typeof ev.detail.callback !== 'function') {
            console.error('Invalid event detail for screenshot capture');
            return;
        }

        // Wait for a short delay to ensure all rendering is complete.
        setTimeout(async () => {
            try {
                const ctx = contextRef.current;
                if (!ctx) {
                    return;
                }
                const ret = await composeCustomScreenshot(ctx, viewportId);
                ev.detail.callback(ret);
            } catch (e) {
                console.error(e);
            }
        }, 100);
    }

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
}

/**
 * Register a listener for per-viewport screenshot capture events — a
 * targeted capture that returns a base64-encoded PNG via a callback on
 * the event detail.
 *
 * Returns a cleanup function that removes the listener.
 *
 * @param {React.MutableRefObject} contextRef
 * @param {string} viewportId
 * @param {string|undefined} baseEventName - The event name prefix that gets
 *        suffixed with `_${viewportId}` to form the actual event name.
 * @returns {() => void}
 */
export function attachViewportScreenshotListener(contextRef, viewportId, baseEventName) {
    if (!baseEventName || !viewportId) {
        return () => { };
    }

    const eventName = `${baseEventName}_${viewportId}`;

    const handler = (ev) => {
        const ctx = contextRef.current;
        if (!ctx) {
            return;
        }
        const { genericRenderWindow, konvaStage } = ctx;
        if (!(ev.detail && genericRenderWindow && konvaStage)) {
            return;
        }

        const { callback } = ev.detail;
        if (typeof callback !== 'function') {
            console.error('Invalid callback for viewport screenshot capture');
            return;
        }

        try {
            const dpr = window.devicePixelRatio || 1;
            const renderWindow = genericRenderWindow.getRenderWindow();

            const waitForCanvasReady = () => {
                const vtkCanvas = genericRenderWindow
                    .getApiSpecificRenderWindow()
                    .getCanvas();
                if (!vtkCanvas) {
                    console.error('VTK canvas not available for screenshot');
                    callback('');
                    return;
                }

                const konvaCanvas = konvaStage.toCanvas();
                if (!konvaCanvas) {
                    console.error('Konva canvas not available for screenshot');
                    callback('');
                    return;
                }

                const canvasReady =
                    vtkCanvas.width > 0 &&
                    vtkCanvas.height > 0 &&
                    konvaCanvas.width > 0 &&
                    konvaCanvas.height > 0;

                if (!canvasReady) {
                    renderWindow.render();
                    konvaStage.draw();
                    requestAnimationFrame(waitForCanvasReady);
                    return;
                }

                // Double RAF to ensure the browser has fully applied layout + paint before capturing canvas
                requestAnimationFrame(() => {
                    renderWindow.render();
                    konvaStage.draw();

                    requestAnimationFrame(() => {
                        renderWindow.render();
                        konvaStage.draw();

                        const latestVtkCanvas = genericRenderWindow
                            .getApiSpecificRenderWindow()
                            .getCanvas();
                        const latestKonvaCanvas = konvaStage.toCanvas();

                        capture(latestVtkCanvas, latestKonvaCanvas);
                    });
                });
            };

            const capture = (vtkCanvas, konvaCanvas) => {
                const compositeCanvas = document.createElement('canvas');
                const ctx2d = compositeCanvas.getContext('2d');
                if (!ctx2d) {
                    console.error('2D context not available for screenshot');
                    callback('');
                    return;
                }

                compositeCanvas.width = vtkCanvas.width / dpr;
                compositeCanvas.height = vtkCanvas.height / dpr;

                ctx2d.drawImage(
                    vtkCanvas,
                    0,
                    0,
                    compositeCanvas.width,
                    compositeCanvas.height,
                );

                ctx2d.drawImage(
                    konvaCanvas,
                    0,
                    0,
                    compositeCanvas.width,
                    compositeCanvas.height,
                );

                const dataUrl = compositeCanvas.toDataURL('image/png');
                callback(dataUrl.split(',')[1] || '');
            };

            requestAnimationFrame(waitForCanvasReady);
        } catch (error) {
            console.error('Error capturing viewport screenshot:', error);
            callback('');
        }
    };

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
}
